import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ParseKeys, TFunction } from "i18next";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  FormGroup,
  FormControlLabel,
  FormLabel,
  Checkbox,
  RadioGroup,
  Radio,
} from "@mui/material";
import { formatShortDate, formatDateTime } from "../../utils/date-utils";
import { ParticipantRole, ParticipantStatus } from "../../types/events-unified";
import { PAGE_SIZE } from "../../constants/pagination";
import {
  People as PeopleIcon,
  Group as TeamIcon,
  Assessment as AnalyticsIcon,
  Notifications as NotificationsIcon,
  Gavel as ModerationIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
} from "@mui/icons-material";
import {
  eventAdminApi,
  type CreateEventNotificationDto,
  type EventAdminParticipant,
  type EventModerationItem,
  type EventNotification,
  type EventTeamMember,
  type ModerationActionDto,
} from "../../services/api/event-admin.api";
import {
  PARTICIPANT_STATUS_CONFIG,
  PARTICIPANT_STATUS_LABEL_KEYS,
  canExecuteModerationAction,
  getSettableParticipantStatusOptions,
} from "../../constants/event-status.constants";
import { DataTable, type DataTableColumn } from "../ui/DataTable";
import { Link as RouterLink, useSearchParams } from "react-router-dom";

/**
 * Экран говорит на языке пользователя: каждая подпись — ключ словаря
 * `dashboard` (свои тексты экрана) или `events` (статусы участника, общие со
 * страницей события и страницей участников). Прежде подписи были зашиты по-русски
 * и в английском интерфейсе оставались русскими.
 *
 * Словари подписей ниже хранят КЛЮЧИ, а не тексты: язык меняется на лету, а
 * модульная константа вычисляется один раз. Тип ключа — `ParseKeys` этих двух
 * пространств: ключ, которого нет в эталонном (английском) словаре, не
 * скомпилируется.
 */
type AdminT = TFunction<["dashboard", "events"]>;
type AdminKey = ParseKeys<["dashboard", "events"]>;

/**
 * AD-220. Подписи ко всем девяти статусам участника.
 *
 * Тип — `Record<ParticipantStatus, …>`, а не свободный объект: появится
 * десятый статус — здесь не хватит ключа и КОМПИЛЯЦИЯ упадёт. Список строк ниже
 * строится перечислением enum, поэтому новый статус попадёт на экран сам, а не
 * выпадет молча — ровно то, чем прежняя разбивка по четырём корзинам и болела.
 */
const PARTICIPANT_STATUS_LABELS: Record<ParticipantStatus, AdminKey> = {
  [ParticipantStatus.REGISTERED]: "eventAdmin.analytics.statuses.registered",
  [ParticipantStatus.CONFIRMED]: "eventAdmin.analytics.statuses.confirmed",
  [ParticipantStatus.ATTENDED]: "eventAdmin.analytics.statuses.attended",
  [ParticipantStatus.PENDING]: "eventAdmin.analytics.statuses.pending",
  [ParticipantStatus.INVITED]: "eventAdmin.analytics.statuses.invited",
  [ParticipantStatus.WAITLISTED]: "eventAdmin.analytics.statuses.waitlisted",
  [ParticipantStatus.CANCELED]: "eventAdmin.analytics.statuses.cancelled",
  [ParticipantStatus.REJECTED]: "eventAdmin.analytics.statuses.rejected",
  [ParticipantStatus.NO_SHOW]: "eventAdmin.analytics.statuses.no_show",
};

/**
 * Роль участия (AD-472). В сгенерированном контракте поле объявлено как `string`,
 * но колонка `event_participants.role` на бэкенде — перечисление из восьми
 * значений, и словарь для них уже есть: тот же, что читает список участников
 * события. Второй словарь развёл бы подписи одного и того же.
 *
 * Значение вне набора остаётся как пришло: видно и что прислал сервер, и что
 * подписи на него нет. Расхождение «в DTO строка, в схеме перечисление» записано
 * в бэклог — чинится на стороне контракта, а не подписью.
 */
const PARTICIPANT_ROLE_LABELS: Record<ParticipantRole, AdminKey> = {
  [ParticipantRole.ATTENDEE]: "events:participants.role.values.attendee",
  [ParticipantRole.SPEAKER]: "events:participants.role.values.speaker",
  [ParticipantRole.MODERATOR]: "events:participants.role.values.moderator",
  [ParticipantRole.ORGANIZER]: "events:participants.role.values.organizer",
  [ParticipantRole.CO_ORGANIZER]:
    "events:participants.role.values.co_organizer",
  [ParticipantRole.VOLUNTEER]: "events:participants.role.values.volunteer",
  [ParticipantRole.SPONSOR]: "events:participants.role.values.sponsor",
  [ParticipantRole.MEDIA]: "events:participants.role.values.media",
};

const participantRoleLabel = (t: AdminT, role: string): string => {
  const key = PARTICIPANT_ROLE_LABELS[role as ParticipantRole];
  return key === undefined ? role : String(t(key));
};

const PARTICIPANT_STATUS_ROWS = Object.values(ParticipantStatus).map((key) => ({
  key,
  labelKey: PARTICIPANT_STATUS_LABELS[key],
}));

/** Роль и состояние назначения в команде — значения протокола; прежде в
 *  метке показывались сырыми (`co_organizer`, `active`). */
const TEAM_ROLE_LABELS: Record<EventTeamMember["teamRole"], AdminKey> = {
  organizer: "eventAdmin.team.roles.organizer",
  co_organizer: "eventAdmin.team.roles.co_organizer",
  event_admin: "eventAdmin.team.roles.event_admin",
  speaker: "eventAdmin.team.roles.speaker",
  supplier: "eventAdmin.team.roles.supplier",
  executor: "eventAdmin.team.roles.executor",
  volunteer: "eventAdmin.team.roles.volunteer",
  sponsor: "eventAdmin.team.roles.sponsor",
  media: "eventAdmin.team.roles.media",
  attendee: "eventAdmin.team.roles.attendee",
};

const TEAM_STATUS_LABELS: Record<EventTeamMember["status"], AdminKey> = {
  active: "eventAdmin.team.statuses.active",
  inactive: "eventAdmin.team.statuses.inactive",
};

/**
 * O-65. Колонки команды — по полям BE-контракта. `DataTable` вызывает
 * `render(value, row, index)`: строка — ВТОРОЙ аргумент; прежние колонки брали
 * первый (значение колонки) за строку и читали поля, которых сервер не отдаёт.
 */
const buildTeamColumns = (t: AdminT): DataTableColumn<EventTeamMember>[] => [
  {
    id: "name",
    label: t("eventAdmin.columns.name"),
    render: (_value, row) => `${row.name} ${row.lastName}`,
  },
  {
    id: "email",
    label: t("eventAdmin.columns.email"),
  },
  {
    id: "teamRole",
    label: t("eventAdmin.columns.role"),
    render: (_value, row) => (
      <Chip
        label={t(TEAM_ROLE_LABELS[row.teamRole])}
        size="small"
        color={
          row.teamRole === "organizer"
            ? "primary"
            : row.teamRole === "co_organizer"
              ? "secondary"
              : "default"
        }
      />
    ),
  },
  {
    id: "status",
    label: t("eventAdmin.columns.status"),
    render: (_value, row) => (
      <Chip
        label={t(TEAM_STATUS_LABELS[row.status])}
        size="small"
        color={row.status === "active" ? "success" : "default"}
      />
    ),
  },
  {
    id: "addedAt",
    label: t("eventAdmin.columns.assignedAt"),
    render: (_value, row) => formatShortDate(row.addedAt),
  },
];

/**
 * AD-188. Управляющая смена статуса участника прямо из строки списка.
 *
 * Предлагаются ТОЛЬКО переходы, которые разрешает карта EVT-004 из текущего
 * статуса; терминальный статус (`attended`/`cancelled`/`rejected`/`no_show`)
 * исходящих переходов не имеет — список блокируется. Прежде в строке стояли две
 * кнопки, «подтвердить» и «отменить», и вторая предлагалась всегда: переход
 * `registered → cancelled` карты не проходит, сервер отвечает на него 400, то
 * есть кнопка была видима, нажималась и не работала.
 *
 * Адрес запроса строится по `participant.id` — ключу СТРОКИ УЧАСТИЯ. `userId`
 * на этом экране не адресует ничего (см. `event-admin.api.ts`).
 */
const ParticipantStatusSelect: React.FC<{
  participant: EventAdminParticipant;
  onChange: (participationId: string, status: ParticipantStatus) => void;
  t: AdminT;
}> = ({ participant, onChange, t }) => {
  const options = getSettableParticipantStatusOptions(participant.status);
  return (
    <FormControl size="small" sx={{ minWidth: 170 }}>
      <Select<string>
        value=""
        displayEmpty
        disabled={options.length === 0}
        renderValue={() => t("eventAdmin.participants.changeStatus")}
        inputProps={{
          "aria-label": t("eventAdmin.participants.changeStatusFor", {
            name: `${participant.name} ${participant.lastName}`,
          }),
        }}
        onChange={(event) => {
          // Значение из DOM — строка; целевой статус берётся из списка валидных
          // переходов, а не приводится типом: значение вне списка тогда просто
          // ничего не делает, вместо запроса, который сервер отвергнет.
          const target = options.find(
            (option) => option.value === event.target.value,
          );
          if (target) {
            onChange(participant.id, target.value);
          }
        }}
      >
        {options.map(({ value }) => (
          <MenuItem key={value} value={value}>
            {t(PARTICIPANT_STATUS_LABEL_KEYS[value], { ns: "events" })}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

/**
 * AD-188. Колонки участников — по полям BE-контракта и с сигнатурой `DataTable`.
 *
 * Прежние колонки объявляли `render: (row) => row.user?.firstName` — и первым
 * аргументом получали ЗНАЧЕНИЕ колонки, а не строку (`DataTable` зовёт
 * `render(value, row, index)`). Опциональная цепочка не спасала: `undefined` был
 * сам аргумент. Экран падал целиком, как только в событии появлялся хотя бы один
 * участник, а вкладка открыта по умолчанию — недоступен был и весь остальной
 * экран. Вложенного `user` сервер не отдаёт вовсе: ответ плоский.
 */
const buildParticipantColumns = (
  onStatusChange: (participationId: string, status: ParticipantStatus) => void,
  t: AdminT,
): DataTableColumn<EventAdminParticipant>[] => [
  {
    id: "name",
    label: t("eventAdmin.columns.name"),
    sortable: true,
    render: (_value, row) => `${row.name} ${row.lastName}`,
  },
  {
    id: "email",
    label: t("eventAdmin.columns.email"),
  },
  {
    id: "role",
    label: t("eventAdmin.columns.role"),
    render: (_value, row) => (
      <Chip
        label={participantRoleLabel(t, row.role)}
        size="small"
        color="primary"
      />
    ),
  },
  {
    id: "status",
    label: t("eventAdmin.columns.status"),
    // Подпись — из словаря, цвет — из общей таблицы статусов; сырое
    // `registered` в интерфейсе администратора не показывается. Обе таблицы
    // объявлены как Record<ParticipantStatus, …>: появится десятый статус — не
    // хватит ключа и упадёт компиляция, а не молча отрисуется пустая метка.
    render: (_value, row) => (
      <Chip
        label={t(PARTICIPANT_STATUS_LABEL_KEYS[row.status], { ns: "events" })}
        size="small"
        color={PARTICIPANT_STATUS_CONFIG[row.status].color}
      />
    ),
  },
  {
    id: "registeredAt",
    label: t("eventAdmin.columns.registeredAt"),
    sortable: true,
    render: (_value, row) => formatShortDate(row.registeredAt),
  },
  {
    id: "actions",
    label: t("eventAdmin.columns.actions"),
    render: (_value, row) => (
      <ParticipantStatusSelect
        participant={row}
        onChange={onStatusChange}
        t={t}
      />
    ),
  },
];

/**
 * AD-188. Подписи к значениям контракта уведомлений и модерации.
 *
 * Тип каждого словаря — `Record<union из генерата, ключ>`: сервер добавит
 * вариант — здесь не хватит ключа и упадёт КОМПИЛЯЦИЯ, а не отрисуется пустая
 * ячейка. Прежний экран показывал сырые значения протокола (`event_update`,
 * `registration`) и брал их из выдуманного зеркала, а не из контракта.
 */
const NOTIFICATION_TYPE_LABELS: Record<EventNotification["type"], AdminKey> = {
  event_update: "eventAdmin.notifications.types.event_update",
  schedule_change: "eventAdmin.notifications.types.schedule_change",
  participant_action: "eventAdmin.notifications.types.participant_action",
  team_announcement: "eventAdmin.notifications.types.team_announcement",
  reminder: "eventAdmin.notifications.types.reminder",
  urgent: "eventAdmin.notifications.types.urgent",
};

const NOTIFICATION_STATUS_LABELS: Record<
  EventNotification["status"],
  {
    labelKey: AdminKey;
    color: "default" | "info" | "warning" | "success" | "error";
  }
> = {
  draft: {
    labelKey: "eventAdmin.notifications.statuses.draft",
    color: "default",
  },
  scheduled: {
    labelKey: "eventAdmin.notifications.statuses.scheduled",
    color: "info",
  },
  sending: {
    labelKey: "eventAdmin.notifications.statuses.sending",
    color: "warning",
  },
  sent: {
    labelKey: "eventAdmin.notifications.statuses.sent",
    color: "success",
  },
  failed: {
    labelKey: "eventAdmin.notifications.statuses.failed",
    color: "error",
  },
};

const NOTIFICATION_PRIORITY_LABELS: Record<
  EventNotification["priority"],
  AdminKey
> = {
  low: "eventAdmin.notifications.priorities.low",
  normal: "eventAdmin.notifications.priorities.normal",
  high: "eventAdmin.notifications.priorities.high",
  urgent: "eventAdmin.notifications.priorities.urgent",
};

const NOTIFICATION_CHANNEL_LABELS: Record<
  EventNotification["channels"][number],
  AdminKey
> = {
  email: "eventAdmin.notifications.channels.email",
  sms: "eventAdmin.notifications.channels.sms",
  push: "eventAdmin.notifications.channels.push",
  in_app: "eventAdmin.notifications.channels.in_app",
};

/** Порядок опций в формах — перечислением словарей, чтобы новый вариант
 *  контракта появлялся на экране сам, а не выпадал молча. */
const NOTIFICATION_TYPE_OPTIONS = Object.entries(NOTIFICATION_TYPE_LABELS);
const NOTIFICATION_PRIORITY_OPTIONS = Object.entries(
  NOTIFICATION_PRIORITY_LABELS,
);
const NOTIFICATION_CHANNEL_OPTIONS = Object.entries(
  NOTIFICATION_CHANNEL_LABELS,
);

/**
 * AD-188. Состояние формы создания уведомления.
 *
 * Хранится отдельно от тела запроса: «когда отправить» — выбор человека, а в
 * контракте это пара `sendImmediately`/`scheduledAt`; значение поля
 * `datetime-local` — местное время без пояса, и в тело оно уходит ISO-строкой.
 */
interface NotificationForm {
  type: CreateEventNotificationDto["type"];
  priority: CreateEventNotificationDto["priority"];
  title: string;
  content: string;
  channels: CreateEventNotificationDto["channels"];
  when: "now" | "scheduled";
  scheduledLocal: string;
}

/** Каналы не выбраны заранее: рассылка платными SMS не должна уходить по
 *  умолчанию, если человек их не отметил. */
const EMPTY_NOTIFICATION_FORM: NotificationForm = {
  type: "event_update",
  priority: "normal",
  title: "",
  content: "",
  channels: [],
  when: "now",
  scheduledLocal: "",
};

/** Значение из DOM — строка; до ключа словаря оно сужается проверкой, а не
 *  приведением типа: значение вне словаря в форму просто не попадает. */
const isKeyOf = <K extends string>(
  labels: Record<K, unknown>,
  value: string,
): value is K => Object.prototype.hasOwnProperty.call(labels, value);

/**
 * Почему форму нельзя отправить; `null` — можно. Кнопка блокируется, а причина
 * пишется рядом: серая кнопка без объяснения ничего человеку не говорит. Пустые
 * тема и текст сервер принял бы (`@IsString` пустую строку пропускает), поэтому
 * проверка здесь — не дублирование серверной.
 */
const notificationFormProblem = (
  form: NotificationForm,
  now: number,
): AdminKey | null => {
  if (form.title.trim() === "") {
    return "eventAdmin.notifications.problems.subject";
  }
  if (form.content.trim() === "") {
    return "eventAdmin.notifications.problems.message";
  }
  if (form.channels.length === 0) {
    return "eventAdmin.notifications.problems.channels";
  }
  if (form.when === "scheduled") {
    const at = new Date(form.scheduledLocal).getTime();
    if (Number.isNaN(at)) {
      return "eventAdmin.notifications.problems.scheduledAt";
    }
    // Прошедшее время сервер не планирует, а рассылает сразу
    // (`createCampaign`): «запланировать на вчера» молча стало бы «отправить».
    if (at <= now) {
      return "eventAdmin.notifications.problems.scheduledInPast";
    }
  }
  return null;
};

/** Тело запроса — ровно генерат BE-контракта. Получатели не передаются: тогда
 *  сервер рассылает всем участникам события, и диалог говорит об этом прямо. */
const buildNotificationBody = (
  form: NotificationForm,
): CreateEventNotificationDto => ({
  type: form.type,
  priority: form.priority,
  title: form.title.trim(),
  content: form.content.trim(),
  channels: form.channels,
  ...(form.when === "now"
    ? { sendImmediately: true }
    : {
        sendImmediately: false,
        scheduledAt: new Date(form.scheduledLocal).toISOString(),
      }),
});

const MODERATION_CONTENT_TYPE_LABELS: Record<
  EventModerationItem["contentType"],
  AdminKey
> = {
  message: "eventAdmin.moderation.contentTypes.message",
  comment: "eventAdmin.moderation.contentTypes.comment",
  post: "eventAdmin.moderation.contentTypes.post",
  media: "eventAdmin.moderation.contentTypes.media",
  link: "eventAdmin.moderation.contentTypes.link",
  registration: "eventAdmin.moderation.contentTypes.registration",
};

const MODERATION_STATUS_LABELS: Record<
  EventModerationItem["status"],
  { labelKey: AdminKey; color: "default" | "warning" | "success" | "error" }
> = {
  pending: {
    labelKey: "eventAdmin.moderation.statuses.pending",
    color: "warning",
  },
  approved: {
    labelKey: "eventAdmin.moderation.statuses.approved",
    color: "success",
  },
  rejected: {
    labelKey: "eventAdmin.moderation.statuses.rejected",
    color: "error",
  },
  hidden: {
    labelKey: "eventAdmin.moderation.statuses.hidden",
    color: "default",
  },
  deleted: {
    labelKey: "eventAdmin.moderation.statuses.deleted",
    color: "default",
  },
};

/**
 * AD-188. Колонки истории уведомлений — по полям контракта и с сигнатурой
 * `render(value, row, index)`. Прежние колонки читали первый аргумент как строку
 * (тот же дефект, что ронял вкладку участников) и обращались к `subject` и
 * `recipients` — полей с такими именами у сервера нет: заголовок называется
 * `title`, число получателей — `recipientsCount`, а `type` означает повод
 * рассылки, а не канал доставки.
 */
const buildNotificationColumns = (
  t: AdminT,
): DataTableColumn<EventNotification>[] => [
  {
    id: "title",
    label: t("eventAdmin.columns.subject"),
    sortable: true,
  },
  {
    id: "type",
    label: t("eventAdmin.columns.reason"),
    render: (_value, row) => (
      <Chip label={t(NOTIFICATION_TYPE_LABELS[row.type])} size="small" />
    ),
  },
  {
    id: "channels",
    label: t("eventAdmin.columns.channels"),
    render: (_value, row) => (
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        {row.channels.map((channel) => (
          <Chip
            key={channel}
            label={t(NOTIFICATION_CHANNEL_LABELS[channel])}
            size="small"
            variant="outlined"
          />
        ))}
      </Box>
    ),
  },
  {
    id: "status",
    label: t("eventAdmin.columns.status"),
    render: (_value, row) => (
      <Chip
        label={t(NOTIFICATION_STATUS_LABELS[row.status].labelKey)}
        size="small"
        color={NOTIFICATION_STATUS_LABELS[row.status].color}
      />
    ),
  },
  {
    id: "recipientsCount",
    label: t("eventAdmin.columns.delivery"),
    // Одного числа получателей мало: часть адресатов сервер пропускает по
    // дедупу, часть — отваливается ошибкой, и `recipientsCount` = sent +
    // skipped + failed. Показать только «Получателей: N» значило бы выдать
    // намерение за результат.
    render: (_value, row) => (
      <Box>
        <Typography variant="body2">
          {t("eventAdmin.notifications.delivered", {
            sent: row.sentCount,
            total: row.recipientsCount,
          })}
        </Typography>
        {(row.skippedCount > 0 || row.failedCount > 0) && (
          <Typography variant="caption" color="text.secondary">
            {t("eventAdmin.notifications.deliveryIssues", {
              skipped: row.skippedCount,
              failed: row.failedCount,
            })}
          </Typography>
        )}
      </Box>
    ),
  },
  {
    id: "createdAt",
    label: t("eventAdmin.columns.createdAt"),
    sortable: true,
    render: (_value, row) => formatDateTime(row.createdAt),
  },
];

/**
 * AD-188. Колонки очереди модерации — по полям контракта.
 *
 * Действий у сервера шесть, но исполняются им только `approve` и `reject`
 * (`approve` переводит участника в «подтверждён», `reject` — в «отменён»);
 * `hide`/`delete`/`warn`/`ban` он отвергает с 400 (`AD-261`; до той правки —
 * принимал и молча ничего не делал, отсюда прежняя формулировка здесь). Поэтому
 * на экране только две кнопки — показывать остальные значило бы обещать
 * действие, которого не происходит. Прежняя третья кнопка отправляла `flag` —
 * значения, которого у сервера нет вовсе.
 *
 * AD-288, интерим. Двух кнопок мало: исполнимость зависит ещё и от СТРОКИ.
 * Сервер проводит действие по карте переходов статуса участника, и на строке
 * «Одобрено» (= участник подтверждён) «Отклонить» даёт 400, а «Одобрить» —
 * успех без смены статуса. Обе кнопки там мертвы, поэтому их нет. На строках
 * «Ожидает решения» обе остаются: за этим значением сервер прячет восемь разных
 * статусов участника, и среди них есть те, где действие проходит. Правило —
 * `canExecuteModerationAction`, там же разобрано, почему интерим закрывает
 * только половину и что закроет остаток (`AD-260`).
 */
const buildModerationColumns = (
  onAction: (contentId: string, action: ModerationActionDto["action"]) => void,
  t: AdminT,
): DataTableColumn<EventModerationItem>[] => [
  {
    id: "contentType",
    label: t("eventAdmin.columns.contentType"),
    render: (_value, row) => (
      <Chip
        label={t(MODERATION_CONTENT_TYPE_LABELS[row.contentType])}
        size="small"
      />
    ),
  },
  {
    id: "content",
    label: t("eventAdmin.columns.content"),
  },
  {
    id: "author",
    label: t("eventAdmin.columns.author"),
    render: (_value, row) => row.author.name,
  },
  {
    id: "status",
    label: t("eventAdmin.columns.status"),
    render: (_value, row) => (
      <Chip
        label={t(MODERATION_STATUS_LABELS[row.status].labelKey)}
        size="small"
        color={MODERATION_STATUS_LABELS[row.status].color}
      />
    ),
  },
  {
    id: "createdAt",
    label: t("eventAdmin.columns.submittedAt"),
    sortable: true,
    render: (_value, row) => formatShortDate(row.createdAt),
  },
  {
    id: "actions",
    label: t("eventAdmin.columns.actions"),
    render: (_value, row) => {
      const canApprove = canExecuteModerationAction(row.status, "approve");
      const canReject = canExecuteModerationAction(row.status, "reject");

      // Пустая ячейка молчит о причине, поэтому её место занимает подпись:
      // строка не «сломана», просто делать с ней из очереди нечего.
      if (!canApprove && !canReject) {
        return (
          <Typography variant="body2" color="text.secondary">
            {t("eventAdmin.moderation.noActions")}
          </Typography>
        );
      }

      return (
        <Box sx={{ display: "flex", gap: 1 }}>
          {canApprove && (
            <IconButton
              size="small"
              color="success"
              onClick={() => onAction(row.id, "approve")}
              title={t("eventAdmin.moderation.approve")}
            >
              <ApproveIcon />
            </IconButton>
          )}
          {canReject && (
            <IconButton
              size="small"
              color="error"
              onClick={() => onAction(row.id, "reject")}
              title={t("eventAdmin.moderation.reject")}
            >
              <RejectIcon />
            </IconButton>
          )}
        </Box>
      );
    },
  },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`event-admin-tabpanel-${index}`}
      aria-labelledby={`event-admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const EventAdminDashboard: React.FC = () => {
  // Прежде тексты брались через `ts(key, fallback)` с русским запасным текстом,
  // а большая часть экрана была зашита по-русски вовсе. Все ключи ниже есть в
  // эталонном словаре — это проверяет компилятор, запасной текст не нужен.
  const { t } = useTranslation(["dashboard", "events"]);
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("eventId") || "";

  const [activeTab, setActiveTab] = useState(0);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationForm, setNotificationForm] = useState<NotificationForm>(
    EMPTY_NOTIFICATION_FORM,
  );
  /**
   * AD-188. Результат действия сообщается Snackbar-ом, а не `alert()`: модальный
   * `alert` блокирует поток страницы, не имеет варианта «ошибка» и в мобильной
   * оболочке (Capacitor) выглядит системным диалогом браузера.
   */
  const [notice, setNotice] = useState<{
    severity: "success" | "error";
    text: string;
  } | null>(null);

  // Загрузка участников
  const { data: participantsData, isLoading: isLoadingParticipants } = useQuery(
    {
      queryKey: ["event-admin", "participants", eventId],
      queryFn: async () => {
        const response = await eventAdminApi.getParticipants({
          eventId,
          limit: PAGE_SIZE.LARGE,
        });
        return response.data;
      },
      enabled: !!eventId,
    },
  );

  // Загрузка команды
  const { data: teamData, isLoading: isLoadingTeam } = useQuery({
    queryKey: ["event-admin", "team", eventId],
    queryFn: async () => {
      const response = await eventAdminApi.getTeam({ eventId });
      return response.data;
    },
    enabled: !!eventId,
  });

  // Загрузка метрик
  const { data: metricsData, isLoading: isLoadingMetrics } = useQuery({
    queryKey: ["event-admin", "metrics", eventId],
    queryFn: async () => {
      const response = await eventAdminApi.getMetrics({ eventId });
      return response.data;
    },
    enabled: !!eventId,
    refetchInterval: 60000,
  });

  /**
   * AD-220. «Участвуют» — это подтвердившие ПЛЮС уже отмеченные на месте.
   * Сумма считается здесь, а не на сервере, намеренно: сервер отдаёт статусы как
   * они есть, а склейка «кто участвует» — решение экрана, и подписью оно названо
   * честно. Переход `registered → attended` разрешён напрямую, минуя подтверждение,
   * поэтому назвать эту сумму «Подтвердили» было бы ложью.
   */
  const participatingParticipants =
    (metricsData?.confirmedParticipants ?? 0) +
    (metricsData?.attendedParticipants ?? 0);

  // Загрузка уведомлений
  const { data: notificationsData, isLoading: isLoadingNotifications } =
    useQuery({
      queryKey: ["event-admin", "notifications", eventId],
      queryFn: async () => {
        const response = await eventAdminApi.getNotifications({
          eventId,
          limit: PAGE_SIZE.DEFAULT,
        });
        return response.data;
      },
      enabled: !!eventId,
    });

  // Загрузка контента для модерации
  const { data: moderationData, isLoading: isLoadingModeration } = useQuery({
    queryKey: ["event-admin", "moderation", eventId],
    queryFn: async () => {
      const response = await eventAdminApi.getModerationItems({
        eventId,
        // Статус модерации — свой набор значений, не статус участника: прежде
        // сюда подставлялось значение из enum участников, совпадавшее по
        // написанию случайно.
        status: "pending",
        limit: PAGE_SIZE.DEFAULT,
      });
      return response.data;
    },
    enabled: !!eventId,
  });

  // Мутация обновления статуса участника
  const updateParticipantMutation = useMutation({
    mutationFn: ({
      participantId,
      status,
    }: {
      participantId: string;
      status: ParticipantStatus;
    }) =>
      // AD-188: `eventId` — обязательный параметр запроса у сервера
      // (`@Query('eventId')`): без него он не найдёт строку участия и не
      // проверит право администрировать событие. Клиент его не передавал.
      eventAdminApi.updateParticipantStatus(participantId, eventId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["event-admin", "participants"],
      });
      queryClient.invalidateQueries({ queryKey: ["event-admin", "metrics"] });
      setNotice({
        severity: "success",
        text: t("eventAdmin.participants.statusUpdated"),
      });
    },
    // Отказ сервера прежде не показывался никак: экран молчал, список оставался
    // прежним, и администратор не мог отличить «не сохранилось» от «не обновился».
    onError: (error: unknown) => {
      setNotice({
        severity: "error",
        text:
          error instanceof Error
            ? t("eventAdmin.participants.statusUpdateFailed", {
                message: error.message,
              })
            : t("eventAdmin.participants.statusUpdateFailedGeneric"),
      });
    },
  });

  // Мутация модерации
  const moderationMutation = useMutation({
    mutationFn: ({
      contentId,
      action,
    }: {
      contentId: string;
      action: ModerationActionDto["action"];
    }) =>
      // AD-188: маршрут без `/action` и с обязательным `eventId` — см. клиент.
      eventAdminApi.performModerationAction(contentId, eventId, { action }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["event-admin", "moderation"],
      });
      setNotice({
        severity: "success",
        text: t("eventAdmin.moderation.done"),
      });
    },
    onError: (error: unknown) => {
      setNotice({
        severity: "error",
        text:
          error instanceof Error
            ? t("eventAdmin.moderation.failed", { message: error.message })
            : t("eventAdmin.moderation.failedGeneric"),
      });
    },
  });

  // Мутация создания уведомления
  const createNotificationMutation = useMutation({
    mutationFn: (body: CreateEventNotificationDto) =>
      // AD-188: `eventId` — параметр запроса, а не поле тела (см. клиент).
      eventAdminApi.createNotification(eventId, body),
    onSuccess: (_response, body) => {
      queryClient.invalidateQueries({
        queryKey: ["event-admin", "notifications"],
      });
      setNotificationDialogOpen(false);
      setNotificationForm(EMPTY_NOTIFICATION_FORM);
      setNotice({
        severity: "success",
        text: body.scheduledAt
          ? t("eventAdmin.notifications.scheduledNotice")
          : t("eventAdmin.notifications.sentNotice"),
      });
    },
    // Диалог остаётся открытым с введённым текстом: при отказе сервера человек
    // исправляет и повторяет, а не набирает сообщение заново.
    onError: (error: unknown) => {
      setNotice({
        severity: "error",
        text:
          error instanceof Error
            ? t("eventAdmin.notifications.createFailed", {
                message: error.message,
              })
            : t("eventAdmin.notifications.createFailedGeneric"),
      });
    },
  });

  const notificationProblem = notificationFormProblem(
    notificationForm,
    Date.now(),
  );

  const closeNotificationDialog = () => {
    if (!createNotificationMutation.isPending) {
      setNotificationDialogOpen(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleParticipantStatusChange = (
    participantId: string,
    status: ParticipantStatus,
  ) => {
    updateParticipantMutation.mutate({ participantId, status });
  };

  const handleModerationAction = (
    contentId: string,
    action: ModerationActionDto["action"],
  ) => {
    moderationMutation.mutate({ contentId, action });
  };

  if (!eventId) {
    return (
      <Box sx={{ p: 3 }}>
        {/*
          AD-188. Прежде здесь было «Пожалуйста, выберите событие» — и способа
          выбрать не было: из меню на экран не ведёт ничего, а список «моих
          событий» (`GET /events/my`) отдаёт только события, где пользователь —
          организатор, и администратора события из команды не видит. Вход на
          экран — пункт «Администрирование» в меню управления на странице
          события, под тем же правом, по которому сервер пускает сюда.
        */}
        <Alert
          severity="warning"
          action={
            <Button
              component={RouterLink}
              to="/events"
              color="inherit"
              size="small"
            >
              {t("eventAdmin.noEvent.backToEvents")}
            </Button>
          }
        >
          {/* Название пункта берётся из того же ключа, что и само меню
              события: переименуют пункт — подсказка не разойдётся с ним. */}
          {t("eventAdmin.noEvent.message", {
            menuItem: t("events:details.administration"),
          })}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Заголовок */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {t("eventAdmin.title")}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t("eventAdmin.subtitle")}
        </Typography>
        {/*
          AD-296. Экран вёл только внутрь себя: вернуться к событию или
          перейти к его публичной регистрации отсюда было нельзя. Обе кнопки
          под тем же правом, что и сам экран (`EVENT_MANAGEMENT_ACCESS`), —
          на `/events/:id/qr` сервер пускает по нему же (`canManageEvent`).
        */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
          <Button
            component={RouterLink}
            to={`/events/${eventId}`}
            variant="outlined"
            size="small"
          >
            {t("eventAdmin.openEvent")}
          </Button>
          <Button
            component={RouterLink}
            to={`/events/${eventId}/qr`}
            variant="outlined"
            size="small"
          >
            {t("eventAdmin.publicRegistration")}
          </Button>
        </Box>
      </Box>

      {/* KPI карточки */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t("eventAdmin.kpis.totalParticipants")}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingMetrics ? (
                      <CircularProgress size={24} />
                    ) : (
                      (metricsData?.totalParticipants ?? 0)
                    )}
                  </Typography>
                  {/*
                    AD-220. Подпись раньше говорила «Подтверждено: N» и брала строго
                    `confirmed` — в день события это число ПАДАЛО: человека отмечали
                    пришедшим, статус уходил в `attended`, и он пропадал из подписи.
                    Участвуют = подтвердили + уже на месте; слагаемые показаны рядом,
                    чтобы «Участвуют» не выглядело как «подтвердили столько».
                  */}
                  <Typography
                    variant="caption"
                    color="success.main"
                    display="block"
                  >
                    {t("eventAdmin.kpis.participating", {
                      number: participatingParticipants,
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("eventAdmin.kpis.participatingBreakdown", {
                      confirmed: metricsData?.confirmedParticipants ?? 0,
                      attended: metricsData?.attendedParticipants ?? 0,
                    })}
                  </Typography>
                </Box>
                <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t("eventAdmin.kpis.teamMembers")}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingTeam ? (
                      <CircularProgress size={24} />
                    ) : (
                      teamData?.total || 0
                    )}
                  </Typography>
                </Box>
                <TeamIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t("eventAdmin.kpis.pendingApprovals")}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingMetrics ? (
                      <CircularProgress size={24} />
                    ) : (
                      (metricsData?.pendingParticipants ?? 0)
                    )}
                  </Typography>
                  {/*
                    AD-220: «ожидают» — это `pending` + `invited`, те, по кому решения
                    нет. Зарегистрировавшиеся сюда больше не входят, у них своя строка
                    ниже: смешивать «не ответил на приглашение» и «зарегистрировался,
                    но не подтвердил» — значит завышать очередь на подтверждение.
                  */}
                  <Typography variant="caption" color="text.secondary">
                    {t("eventAdmin.kpis.registered", {
                      number: metricsData?.registeredParticipants ?? 0,
                    })}
                  </Typography>
                </Box>
                <NotificationsIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t("eventAdmin.kpis.moderationQueue")}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingModeration ? (
                      <CircularProgress size={24} />
                    ) : (
                      moderationData?.total || 0
                    )}
                  </Typography>
                </Box>
                <ModerationIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Вкладки */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab
              label={t("eventAdmin.tabs.participants")}
              icon={<PeopleIcon />}
              iconPosition="start"
            />
            <Tab
              label={t("eventAdmin.tabs.team")}
              icon={<TeamIcon />}
              iconPosition="start"
            />
            <Tab
              label={t("eventAdmin.tabs.analytics")}
              icon={<AnalyticsIcon />}
              iconPosition="start"
            />
            <Tab
              label={t("eventAdmin.tabs.notifications")}
              icon={<NotificationsIcon />}
              iconPosition="start"
            />
            <Tab
              label={t("eventAdmin.tabs.moderation")}
              icon={<ModerationIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Вкладка: Участники */}
        <TabPanel value={activeTab} index={0}>
          {isLoadingParticipants ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : participantsData?.participants &&
            participantsData.participants.length > 0 ? (
            <DataTable<EventAdminParticipant>
              title={t("eventAdmin.participantsTitle")}
              columns={buildParticipantColumns(
                handleParticipantStatusChange,
                t,
              )}
              data={participantsData.participants}
              rowKey={(row) => row.id}
              pagination
              searchable
            />
          ) : (
            <Alert severity="info">{t("eventAdmin.participants.empty")}</Alert>
          )}
        </TabPanel>

        {/* Вкладка: Команда */}
        <TabPanel value={activeTab} index={1}>
          {isLoadingTeam ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : teamData?.team && teamData.team.length > 0 ? (
            <DataTable<EventTeamMember>
              title={t("eventAdmin.teamTitle")}
              columns={buildTeamColumns(t)}
              data={teamData.team}
              rowKey={(row) => row.id}
            />
          ) : (
            <Alert severity="info">{t("eventAdmin.team.empty")}</Alert>
          )}
        </TabPanel>

        {/* Вкладка: Аналитика */}
        <TabPanel value={activeTab} index={2}>
          {isLoadingMetrics ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : metricsData ? (
            /*
              AD-194/AD-220. Здесь были «Доходы» и «Вовлеченность» — выручка,
              продажа билетов, уникальные посетители, просмотры страниц, средняя
              длительность сессии, bounce rate. Ни одного источника у этих чисел
              нет: сервер их не отдавал никогда, экран читал `undefined` и рисовал
              нули и «0.0 %» — то есть утверждал, что доход равен нулю, а посетителей
              не было. Разделы сняты целиком, а не заморожены нулями; вместо них —
              то, что система действительно знает.
            */
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t("eventAdmin.analytics.byStatus")}
                    </Typography>
                    {PARTICIPANT_STATUS_ROWS.map(({ key, labelKey }) => (
                      <Box
                        key={key}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          py: 0.25,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {t(labelKey)}
                        </Typography>
                        <Typography variant="body2">
                          {metricsData.participantsByStatus[key]}
                        </Typography>
                      </Box>
                    ))}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        pt: 1,
                        mt: 1,
                        borderTop: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="body2">
                        {t("eventAdmin.analytics.total")}
                      </Typography>
                      <Typography variant="body2">
                        {metricsData.totalParticipants}
                      </Typography>
                    </Box>
                    {/*
                      «Не пришли» — вывод, а не статус, и сервер отдаёт его ТОЛЬКО у
                      завершённого события. Поэтому проверяется наличие поля, а не
                      его значение: ноль на идущем событии читался бы как «все
                      пришли», хотя чек-ин ещё не начинался.
                    */}
                    {metricsData.notAttendedParticipants !== undefined && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          pt: 0.5,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {t("eventAdmin.analytics.notAttended")}
                        </Typography>
                        <Typography variant="body2">
                          {metricsData.notAttendedParticipants}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t("eventAdmin.analytics.tasks")}
                    </Typography>
                    <Typography variant="h5" color="primary">
                      {t("eventAdmin.analytics.tasksDone", {
                        completed: metricsData.teamPerformance.completedTasks,
                        total: metricsData.teamPerformance.totalTasks,
                      })}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {t("eventAdmin.analytics.tasksRate", {
                        rate: metricsData.teamPerformance.taskCompletionRate,
                      })}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={
                        metricsData.teamPerformance.overdueTasks > 0
                          ? "error.main"
                          : "text.secondary"
                      }
                      sx={{ mt: 1 }}
                    >
                      {t("eventAdmin.analytics.overdue", {
                        number: metricsData.teamPerformance.overdueTasks,
                      })}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t("eventAdmin.analytics.roles")}
                    </Typography>
                    {/*
                      Пустой список и ноль — разные утверждения. Раньше здесь были
                      три выдуманные строки («Участник 60 % / Спикер 25 %»), теперь
                      это группировка по колонке, и при отсутствии данных экран так
                      и говорит.
                    */}
                    {metricsData.participantsByRole.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        {t("eventAdmin.analytics.noParticipants")}
                      </Typography>
                    ) : (
                      metricsData.participantsByRole.map((row) => (
                        <Box
                          key={row.role}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            py: 0.25,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {participantRoleLabel(t, row.role)}
                          </Typography>
                          <Typography variant="body2">
                            {row.count} · {row.percentage}%
                          </Typography>
                        </Box>
                      ))
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t("eventAdmin.analytics.daily")}
                    </Typography>
                    {metricsData.dailyRegistrations.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        {t("eventAdmin.analytics.noRegistrations")}
                      </Typography>
                    ) : (
                      metricsData.dailyRegistrations.map((row) => (
                        <Box
                          key={row.date}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            py: 0.25,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {row.date}
                          </Typography>
                          <Typography variant="body2">
                            {row.registrations}
                          </Typography>
                        </Box>
                      ))
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">{t("eventAdmin.analytics.empty")}</Alert>
          )}
        </TabPanel>

        {/* Вкладка: Уведомления */}
        <TabPanel value={activeTab} index={3}>
          <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              startIcon={<NotificationsIcon />}
              onClick={() => setNotificationDialogOpen(true)}
            >
              {t("eventAdmin.notifications.create")}
            </Button>
          </Box>

          {isLoadingNotifications ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : notificationsData?.notifications &&
            notificationsData.notifications.length > 0 ? (
            <DataTable<EventNotification>
              title={t("eventAdmin.notifications.historyTitle")}
              columns={buildNotificationColumns(t)}
              data={notificationsData.notifications}
              rowKey={(row) => row.id}
              pagination
            />
          ) : (
            <Alert severity="info">{t("eventAdmin.notifications.empty")}</Alert>
          )}
        </TabPanel>

        {/* Вкладка: Модерация */}
        <TabPanel value={activeTab} index={4}>
          {isLoadingModeration ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : moderationData?.items && moderationData.items.length > 0 ? (
            <DataTable<EventModerationItem>
              title={t("eventAdmin.moderation.title")}
              columns={buildModerationColumns(handleModerationAction, t)}
              data={moderationData.items}
              rowKey={(row) => row.id}
              pagination
            />
          ) : (
            <Alert severity="success">{t("eventAdmin.moderation.empty")}</Alert>
          )}
        </TabPanel>
      </Card>

      {/* Диалог создания уведомления */}
      {/*
        AD-188. Прежде поля диалога не имели состояния, «Тип» предлагал
        email/push/sms — КАНАЛЫ, а не повод рассылки, — а у кнопки «Отправить» не
        было обработчика: нажатие не делало ничего. Теперь форма собирает ровно
        тело `CreateEventNotificationDto`.
      */}
      <Dialog
        open={notificationDialogOpen}
        onClose={closeNotificationDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("eventAdmin.notifications.create")}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t("eventAdmin.notifications.form.recipientsNote")}
            </Typography>
            <TextField
              fullWidth
              label={t("eventAdmin.notifications.form.subject")}
              margin="normal"
              id="notification-title"
              value={notificationForm.title}
              // Колонка `title` — varchar(300): длиннее сервер не сохранит.
              inputProps={{ maxLength: 300 }}
              onChange={(event) => {
                const title = event.target.value;
                setNotificationForm((prev) => ({ ...prev, title }));
              }}
            />
            <TextField
              fullWidth
              label={t("eventAdmin.notifications.form.message")}
              margin="normal"
              multiline
              rows={4}
              id="notification-content"
              value={notificationForm.content}
              onChange={(event) => {
                const content = event.target.value;
                setNotificationForm((prev) => ({ ...prev, content }));
              }}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="notification-type-label">
                    {t("eventAdmin.notifications.form.reason")}
                  </InputLabel>
                  <Select<string>
                    labelId="notification-type-label"
                    label={t("eventAdmin.notifications.form.reason")}
                    value={notificationForm.type}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (isKeyOf(NOTIFICATION_TYPE_LABELS, value)) {
                        setNotificationForm((prev) => ({
                          ...prev,
                          type: value,
                        }));
                      }
                    }}
                  >
                    {NOTIFICATION_TYPE_OPTIONS.map(([value, labelKey]) => (
                      <MenuItem key={value} value={value}>
                        {t(labelKey)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="notification-priority-label">
                    {t("eventAdmin.notifications.form.priority")}
                  </InputLabel>
                  <Select<string>
                    labelId="notification-priority-label"
                    label={t("eventAdmin.notifications.form.priority")}
                    value={notificationForm.priority}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (isKeyOf(NOTIFICATION_PRIORITY_LABELS, value)) {
                        setNotificationForm((prev) => ({
                          ...prev,
                          priority: value,
                        }));
                      }
                    }}
                  >
                    {NOTIFICATION_PRIORITY_OPTIONS.map(([value, labelKey]) => (
                      <MenuItem key={value} value={value}>
                        {t(labelKey)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <FormControl component="fieldset" margin="normal" fullWidth>
              <FormLabel component="legend">
                {t("eventAdmin.notifications.form.channels")}
              </FormLabel>
              <FormGroup row>
                {NOTIFICATION_CHANNEL_OPTIONS.map(([value, labelKey]) => (
                  <FormControlLabel
                    key={value}
                    label={t(labelKey)}
                    control={
                      <Checkbox
                        checked={notificationForm.channels.some(
                          (channel) => channel === value,
                        )}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          if (!isKeyOf(NOTIFICATION_CHANNEL_LABELS, value)) {
                            return;
                          }
                          setNotificationForm((prev) => ({
                            ...prev,
                            channels: checked
                              ? [...prev.channels, value]
                              : prev.channels.filter(
                                  (channel) => channel !== value,
                                ),
                          }));
                        }}
                      />
                    }
                  />
                ))}
              </FormGroup>
            </FormControl>
            <FormControl component="fieldset" margin="normal" fullWidth>
              <FormLabel component="legend">
                {t("eventAdmin.notifications.form.when")}
              </FormLabel>
              <RadioGroup
                row
                value={notificationForm.when}
                onChange={(event) => {
                  const when = event.target.value;
                  if (when === "now" || when === "scheduled") {
                    setNotificationForm((prev) => ({ ...prev, when }));
                  }
                }}
              >
                <FormControlLabel
                  value="now"
                  control={<Radio />}
                  label={t("eventAdmin.notifications.form.now")}
                />
                <FormControlLabel
                  value="scheduled"
                  control={<Radio />}
                  label={t("eventAdmin.notifications.form.scheduled")}
                />
              </RadioGroup>
              {notificationForm.when === "scheduled" && (
                <TextField
                  type="datetime-local"
                  label={t("eventAdmin.notifications.form.scheduledAt")}
                  id="notification-scheduled-at"
                  value={notificationForm.scheduledLocal}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 1, maxWidth: 280 }}
                  onChange={(event) => {
                    const scheduledLocal = event.target.value;
                    setNotificationForm((prev) => ({
                      ...prev,
                      scheduledLocal,
                    }));
                  }}
                />
              )}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {notificationProblem !== null && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mr: "auto" }}
            >
              {t(notificationProblem)}
            </Typography>
          )}
          <Button
            onClick={closeNotificationDialog}
            disabled={createNotificationMutation.isPending}
          >
            {t("eventAdmin.notifications.form.cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={
              notificationProblem !== null ||
              createNotificationMutation.isPending
            }
            onClick={() =>
              createNotificationMutation.mutate(
                buildNotificationBody(notificationForm),
              )
            }
          >
            {notificationForm.when === "scheduled"
              ? t("eventAdmin.notifications.form.schedule")
              : t("eventAdmin.notifications.form.send")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar требует ровно один элемент-потомок, поэтому без сообщения он
          не рисуется вовсе, а не получает пустого потомка. */}
      {notice !== null && (
        <Snackbar
          open
          autoHideDuration={5000}
          onClose={() => setNotice(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={notice.severity}
            onClose={() => setNotice(null)}
            sx={{ width: "100%" }}
          >
            {notice.text}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default EventAdminDashboard;
